// Which brand the site is wearing: RaisedBeef (the default) or Cattle Market Central.
//
// DVAuction asked (owner, 2026-10-20) to rebrand RaisedBeef as "Cattle Market Central,
// driven by DVAuction and RaisedBeef", on a new domain. Until the domain moves, the same
// app shows it on a subdomain, and `?brand=cmc` shows it anywhere for a preview.
//
// ⚠️ THE BRAND IS ONE ATTRIBUTE, data-brand ON <html>, SET BEFORE ANYTHING RENDERS. The
// prerendered pages hydrate, so no component may branch on the brand while rendering: the
// server draws RaisedBeef and the browser would disagree. Everything that differs is
// CSS (src/brand/cmc.css, scoped under [data-brand="cmc"], so it does nothing on
// raisedbeef.com), and the few strings that change are set after load.

export type BrandKey = "rb" | "cmc";

export const CMC_NAME = "Cattle Market Central";
export const CMC_TAGLINE = "Driven by DVAuction and RaisedBeef";

/** Hosts that always wear Cattle Market Central. The last two are the domain it is moving to. */
export const CMC_HOSTS = [
  "cattlemarketcentral.raisedbeef.com",
  "cattlemarketcentral.com",
  "www.cattlemarketcentral.com",
];

const STORE = "rb.brand";

/**
 * WHERE THE BRAND MAY SWITCH ON AT ALL (owner, 2026-10-20: "only raisedbeef.com ... you are looking at
 * markets.raisedbeef.ai, we aren't messing with that"). The demo is the raisedbeef.com site. The markets
 * platform (markets.raisedbeef.ai, every other raisedbeef.ai host) never wears it, not even with
 * ?brand=cmc, so a link cannot rebrand it by accident. Allowed: raisedbeef.com and its subdomains, the
 * brand's own hosts, Vercel preview deployments, and local development.
 */
export function brandAllowed(hostname: string): boolean {
  if (CMC_HOSTS.includes(hostname)) return true;
  if (hostname === "raisedbeef.com" || hostname.endsWith(".raisedbeef.com")) return true;
  if (hostname.endsWith(".vercel.app")) return true;
  return hostname === "localhost" || hostname === "127.0.0.1";
}

/** The brand for a hostname and query string; `stored` is what a ?brand= preview left in sessionStorage. */
export function detectBrand(hostname: string, search: string, stored: string | null): BrandKey {
  if (!brandAllowed(hostname)) return "rb";
  if (CMC_HOSTS.includes(hostname)) return "cmc";
  try {
    const q = new URLSearchParams(search).get("brand");
    if (q === "cmc") return "cmc";
    if (q === "rb") return "rb";
  } catch {
    /* no query */
  }
  return stored === "cmc" ? "cmc" : "rb";
}

export type Theme = "light" | "dark";
export const THEME_STORE = "cmc.theme";
/** Light is the default; dark is the visitor's choice (owner, 2026-10-20). */
export const parseTheme = (v: string | null | undefined): Theme => (v === "dark" ? "dark" : "light");

export function readStoredBrand(): string | null {
  try { return window.sessionStorage.getItem(STORE); } catch { return null; }
}
export function storeBrand(b: BrandKey): void {
  try {
    if (b === "cmc") window.sessionStorage.setItem(STORE, "cmc");
    else window.sessionStorage.removeItem(STORE);
  } catch {
    /* a private window: the preview just does not follow the visitor to the next page */
  }
}
